import { InfiniteData, infiniteQueryOptions, UseInfiniteQueryOptions } from "@tanstack/react-query";
import {
  Annotation,
  AnnotationScope,
  Build,
  CursorPaginated,
} from "~/components/build/Show/lib/types";
import fetcher from "~/components/build/Show/lib/fetcher";

export const ALL_ANNOTATIONS_QUERY_KEY_PREFIX = "list" as const;

export const allAnnotationsQueryKey = (buildId: Build["id"], scope: AnnotationScope | null) => {
  // Namespaced under "list" so the AnnotationsPanel's combined query keys don't
  // collide with `allBuildAnnotationsQueryKey` / `allJobAnnotationsQueryKey`,
  // and so all variants can be invalidated together via prefix invalidation.
  return [
    "build",
    buildId,
    "annotations",
    ALL_ANNOTATIONS_QUERY_KEY_PREFIX,
    { scope: scope ?? "any" },
  ] as const;
};

type AllAnnotationsPage = CursorPaginated<Annotation>;
type AllAnnotationsPageParam = string | null;
type AllAnnotationsQueryKey = ReturnType<typeof allAnnotationsQueryKey>;

type AdditionalOptions = Omit<
  UseInfiniteQueryOptions<
    AllAnnotationsPage,
    Error,
    InfiniteData<AllAnnotationsPage, AllAnnotationsPageParam>,
    AllAnnotationsQueryKey,
    AllAnnotationsPageParam
  >,
  "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam"
>;

export const allAnnotationsQueryOptions = (
  build: Build,
  scope: AnnotationScope | null,
  options?: AdditionalOptions,
) => {
  return infiniteQueryOptions({
    ...options,
    queryKey: allAnnotationsQueryKey(build.id, scope),
    queryFn: ({ pageParam, signal }) =>
      fetchAllAnnotations(build.buildDataBasePath, scope, pageParam, signal),
    initialPageParam: null as AllAnnotationsPageParam,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.endCursor : null),
  });
};

function fetchAllAnnotations(
  buildDataBasePath: Build["buildDataBasePath"],
  scope: AnnotationScope | null,
  pageParam: string | null,
  signal: AbortSignal,
) {
  const url = new URL(`${buildDataBasePath}/annotations`, window.location.origin);

  if (scope) {
    url.searchParams.set("scope", scope);
  }

  if (pageParam) {
    url.searchParams.set("after", pageParam);
  }

  return fetcher<CursorPaginated<Annotation>>(url, true, { signal });
}
